import { IconCalendar } from '@tabler/icons-react';
import type { FC } from 'react';
import { DetailSectionHeader } from '@/components/detail-page-primitives';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

/** Undo the primitives' cell padding: these columns run edge to edge of the card body. */
const HEAD_CLASS = 'h-auto px-0 py-2 font-medium text-muted-foreground';
const CELL_CLASS = 'px-0 py-3';

interface ActivityRow {
  period: string;
  amountCents: number;
  payments: number;
  files: number;
  highlight?: boolean;
}

interface ActivitySummaryTableProps {
  rows: ActivityRow[];
  formatCurrency: (cents: number) => string;
}

export const ActivitySummaryTable: FC<ActivitySummaryTableProps> = ({ rows, formatCurrency }) => {
  return (
    <Card className="py-0">
      <CardContent className="p-0">
        <DetailSectionHeader icon={IconCalendar} title="Activity Summary" />
        <div className="p-4">
          <Table caption="Activity summary by period">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className={HEAD_CLASS}>Period</TableHead>
                <TableHead className={cn(HEAD_CLASS, 'text-right')}>Amount</TableHead>
                <TableHead className={cn(HEAD_CLASS, 'text-right')}>Payments</TableHead>
                <TableHead className={cn(HEAD_CLASS, 'text-right')}>Files</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.period}
                  // Hover is suppressed: a row tint here would overwrite `highlight`.
                  className={cn(
                    'hover:bg-transparent',
                    row.highlight && 'bg-primary/5 font-semibold'
                  )}
                >
                  <TableRowHeader className={cn(CELL_CLASS, 'font-medium')}>
                    {row.period}
                  </TableRowHeader>
                  <TableCell className={cn(CELL_CLASS, 'text-right font-semibold text-primary')}>
                    {formatCurrency(row.amountCents)}
                  </TableCell>
                  <TableCell className={cn(CELL_CLASS, 'text-right')}>
                    {row.payments.toLocaleString()}
                  </TableCell>
                  <TableCell className={cn(CELL_CLASS, 'text-right')}>
                    {row.files.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
